import type {
  CellFeature,
  ClassificationRule,
  ClassificationSignal,
  ColumnClassification,
  ColumnFeature,
  ColumnRole,
  FormulaArea,
  RegionClassification,
  RegionFeatures,
  RegionType,
  RowClassification,
  RowFeature,
  RowRole,
} from "./types";
import { COLUMN_ROLE_CHECKS, REGION_TYPE_CHECKS, ROW_ROLE_CHECKS } from "./rules";
import { addressFromRC } from "../../rules/audit/addressing";
import { detectAndPairComponents } from "./components";

// Date number-format detection (same logic as the cell-types module's
// `dateFormatCells` audit rule, inlined here so this module stays self-contained).
const DATE_TOKEN_RE = /[yd]|m{3,}/i;
function isDateFormat(fmt: string | undefined | null): boolean {
  if (!fmt || fmt === "General") return false;
  const stripped = fmt
    .replace(/"[^"]*"/g, "")
    .replace(/\\./g, "")
    .replace(/\[[^\]]*\]/g, "");
  return DATE_TOKEN_RE.test(stripped);
}

/**
 * Raw data describing one region. Caller provides the bounds and the cell
 * arrays trimmed to the region's bounding box.
 *
 * NOTE: `formulasR1C1` must be the R1C1 form (Excel.Range.formulasR1C1), not
 * A1. R1C1 makes copied-down/across formulas string-equal, which is how the
 * classifier detects uniform calculated areas inside a region.
 */
export interface RegionInput {
  minRow: number;
  minCol: number;
  maxRow: number;
  maxCol: number;
  /** rowCount × colCount, sheet-absolute-trimmed. */
  values: unknown[][];
  valueTypes: Excel.RangeValueType[][];
  formulasR1C1: string[][];
  /** Number-format strings — used to distinguish date columns from numeric. */
  numberFormat: string[][];
}

/**
 * Top-level entry point: classify a region using the given (effective) rule set.
 * Rules whose check function isn't found are silently skipped — the descriptor
 * list and check registry are versioned together, but missing entries
 * shouldn't crash classification.
 */
export function classifyRegion(
  region: RegionInput,
  rules: ClassificationRule[]
): RegionClassification {
  const features = computeFeatures(region);

  // -- Region type ----------------------------------------------------------
  const regionScores = new Map<RegionType, ClassificationSignal[]>();
  for (const rule of rules) {
    if (!rule.enabled || rule.target !== "region-type") continue;
    const check = REGION_TYPE_CHECKS[rule.id];
    if (!check) continue;
    const strength = clamp01(check(features));
    if (strength <= 0) continue;
    const contribution = strength * rule.weight;
    const target = rule.emits as RegionType;
    const list = regionScores.get(target) ?? [];
    list.push({
      ruleId: rule.id,
      ruleName: rule.name,
      target,
      weight: rule.weight,
      strength,
      contribution,
    });
    regionScores.set(target, list);
  }
  const {
    value: regionType,
    confidence: regionTypeConfidence,
    signals: regionTypeSignals,
  } = resolveTarget<RegionType>(regionScores, "unknown");

  // -- Row roles ------------------------------------------------------------
  const rows: RowClassification[] = [];
  for (let i = 0; i < features.rowCount; i++) {
    const rowScores = new Map<RowRole, ClassificationSignal[]>();
    for (const rule of rules) {
      if (!rule.enabled || rule.target !== "row-role") continue;
      const check = ROW_ROLE_CHECKS[rule.id];
      if (!check) continue;
      const strength = clamp01(check(features, i));
      if (strength <= 0) continue;
      const contribution = strength * rule.weight;
      const target = rule.emits as RowRole;
      const list = rowScores.get(target) ?? [];
      list.push({
        ruleId: rule.id,
        ruleName: rule.name,
        target,
        weight: rule.weight,
        strength,
        contribution,
      });
      rowScores.set(target, list);
    }
    const { value: role, confidence, signals } = resolveTarget<RowRole>(rowScores, "unknown");
    rows.push({
      rowIndex: i,
      absoluteRow: features.minRow + i,
      role,
      confidence,
      signals,
    });
  }

  // -- Column roles ---------------------------------------------------------
  const columns: ColumnClassification[] = [];
  for (let i = 0; i < features.colCount; i++) {
    const colScores = new Map<ColumnRole, ClassificationSignal[]>();
    for (const rule of rules) {
      if (!rule.enabled || rule.target !== "column-role") continue;
      const check = COLUMN_ROLE_CHECKS[rule.id];
      if (!check) continue;
      const strength = clamp01(check(features, i));
      if (strength <= 0) continue;
      const contribution = strength * rule.weight;
      const target = rule.emits as ColumnRole;
      const list = colScores.get(target) ?? [];
      list.push({
        ruleId: rule.id,
        ruleName: rule.name,
        target,
        weight: rule.weight,
        strength,
        contribution,
      });
      colScores.set(target, list);
    }
    const { value: role, confidence, signals } = resolveTarget<ColumnRole>(colScores, "unknown");
    columns.push({
      colIndex: i,
      absoluteCol: features.minCol + i,
      role,
      confidence,
      signals,
    });
  }

  // -- Components (bottom-up structural pieces) -----------------------------
  const components = detectAndPairComponents(features);
  features.components = components;

  return {
    regionType,
    regionTypeConfidence,
    regionTypeSignals,
    rows,
    columns,
    components,
    formulaAreas: detectFormulaAreas(features),
  };
}

/**
 * Find contiguous (4-connected) groups of cells inside the region that share
 * IDENTICAL R1C1 formulas. The first cell of each area is the seed; BFS
 * grows outward to all neighbors with the same formula string. Result is
 * sorted largest first.
 */
function detectFormulaAreas(features: RegionFeatures): FormulaArea[] {
  const rowCount = features.rowCount;
  const colCount = features.colCount;
  const visited = new Uint8Array(rowCount * colCount);
  const idx = (r: number, c: number): number => r * colCount + c;
  const areas: FormulaArea[] = [];

  for (let r0 = 0; r0 < rowCount; r0++) {
    for (let c0 = 0; c0 < colCount; c0++) {
      if (visited[idx(r0, c0)]) continue;
      const seed = features.rows[r0].cells[c0];
      if (!seed.hasFormula) continue;
      const formula = seed.formula;

      visited[idx(r0, c0)] = 1;
      const queue: number[] = [r0, c0];
      let head = 0;
      let minR = r0;
      let maxR = r0;
      let minC = c0;
      let maxC = c0;
      let count = 0;

      while (head < queue.length) {
        const r = queue[head++];
        const c = queue[head++];
        count++;
        if (r < minR) minR = r;
        if (r > maxR) maxR = r;
        if (c < minC) minC = c;
        if (c > maxC) maxC = c;

        // 4-connected neighbours
        const neighbours: Array<[number, number]> = [
          [r - 1, c],
          [r + 1, c],
          [r, c - 1],
          [r, c + 1],
        ];
        for (const [nr, nc] of neighbours) {
          if (nr < 0 || nr >= rowCount || nc < 0 || nc >= colCount) continue;
          if (visited[idx(nr, nc)]) continue;
          const ncell = features.rows[nr].cells[nc];
          if (!ncell.hasFormula) continue;
          if (ncell.formula !== formula) continue;
          visited[idx(nr, nc)] = 1;
          queue.push(nr, nc);
        }
      }

      const absMinRow = features.minRow + minR;
      const absMinCol = features.minCol + minC;
      const absMaxRow = features.minRow + maxR;
      const absMaxCol = features.minCol + maxC;
      const rectArea = (maxR - minR + 1) * (maxC - minC + 1);
      areas.push({
        formulaR1C1: formula,
        minRow: absMinRow,
        minCol: absMinCol,
        maxRow: absMaxRow,
        maxCol: absMaxCol,
        cellCount: count,
        isRectangular: count === rectArea,
        address:
          absMinRow === absMaxRow && absMinCol === absMaxCol
            ? addressFromRC(absMinRow, absMinCol)
            : `${addressFromRC(absMinRow, absMinCol)}:${addressFromRC(absMaxRow, absMaxCol)}`,
      });
    }
  }

  areas.sort((a, b) => b.cellCount - a.cellCount);
  return areas;
}

/**
 * Pre-compute features for a region: per-cell category, per-row counts,
 * per-column ratios, and a flag for rows containing upward-referencing
 * formulas. Computed once and reused across all rules.
 */
export function computeFeatures(region: RegionInput): RegionFeatures {
  const rowCount = region.maxRow - region.minRow + 1;
  const colCount = region.maxCol - region.minCol + 1;

  // Pass 1: cells + row aggregates
  const rows: RowFeature[] = [];
  for (let r = 0; r < rowCount; r++) {
    const cells: CellFeature[] = [];
    let textCount = 0;
    let numericCount = 0;
    let formulaCount = 0;
    let emptyCount = 0;
    let leftmostText = "";
    for (let c = 0; c < colCount; c++) {
      const cell = makeCellFeature(
        region.valueTypes[r]?.[c],
        region.values[r]?.[c],
        region.formulasR1C1[r]?.[c],
        region.numberFormat[r]?.[c]
      );
      cells.push(cell);
      switch (cell.category) {
        case "empty":
          emptyCount++;
          break;
        case "text":
          textCount++;
          if (!leftmostText && cell.text) leftmostText = cell.text;
          break;
        case "numeric":
        case "date":
          numericCount++;
          break;
        case "formula":
          formulaCount++;
          break;
        // boolean / error: not counted toward text/numeric/formula totals here
      }
    }
    rows.push({
      rowIndex: r,
      absoluteRow: region.minRow + r,
      cells,
      textCount,
      numericCount,
      formulaCount,
      emptyCount,
      leftmostText,
    });
  }

  // Pass 2: column features
  const columnTextRatio = new Array<number>(colCount).fill(0);
  const columnNumericRatio = new Array<number>(colCount).fill(0);
  const columns: ColumnFeature[] = [];
  for (let c = 0; c < colCount; c++) {
    let nonEmpty = 0;
    let textInCol = 0;
    let numericInCol = 0;
    let dateInCol = 0;
    let formulaInCol = 0;
    let booleanInCol = 0;
    let emptyInCol = 0;
    const distinctValues = new Set<string>();
    let firstFormula = "";
    let uniformFormula = true;
    for (let r = 0; r < rowCount; r++) {
      const cell = rows[r].cells[c];
      const cat = cell.category;
      if (cat === "empty") {
        emptyInCol++;
        continue;
      }
      nonEmpty++;
      if (cat === "text") textInCol++;
      else if (cat === "numeric") numericInCol++;
      else if (cat === "date") dateInCol++;
      else if (cat === "formula") formulaInCol++;
      else if (cat === "boolean") booleanInCol++;
      // error: tracked via emptyInCol+nonEmpty count
      const v = cell.value;
      distinctValues.add(v === null || v === undefined ? "" : String(v));
      if (cell.hasFormula) {
        if (!firstFormula) firstFormula = cell.formula;
        else if (cell.formula !== firstFormula) uniformFormula = false;
      }
    }
    if (nonEmpty > 0) {
      columnTextRatio[c] = textInCol / nonEmpty;
      columnNumericRatio[c] = (numericInCol + dateInCol + formulaInCol) / nonEmpty;
    }
    columns.push({
      colIndex: c,
      absoluteCol: region.minCol + c,
      textCount: textInCol,
      numericCount: numericInCol,
      dateCount: dateInCol,
      formulaCount: formulaInCol,
      booleanCount: booleanInCol,
      emptyCount: emptyInCol,
      nonEmptyCount: nonEmpty,
      uniqueness: nonEmpty > 0 ? distinctValues.size / nonEmpty : 0,
      uniformFormula: formulaInCol > 0 ? uniformFormula : false,
    });
  }

  // Pass 3: per-row "has upward formula" — does any formula in this row
  // reference a cell in a row above (within the same column or any column)?
  const rowHasUpwardFormula = new Array<boolean>(rowCount).fill(false);
  for (let r = 1; r < rowCount; r++) {
    for (let c = 0; c < colCount; c++) {
      const f = rows[r].cells[c].formula;
      if (!f) continue;
      if (formulaReferencesAbove(f, r, region.minRow)) {
        rowHasUpwardFormula[r] = true;
        break;
      }
    }
  }

  return {
    minRow: region.minRow,
    minCol: region.minCol,
    maxRow: region.maxRow,
    maxCol: region.maxCol,
    rowCount,
    colCount,
    rows,
    columns,
    columnTextRatio,
    columnNumericRatio,
    rowHasUpwardFormula,
  };
}

function makeCellFeature(
  vt: Excel.RangeValueType | undefined,
  value: unknown,
  formula: string | undefined,
  numberFormat: string | undefined
): CellFeature {
  const hasFormula = typeof formula === "string" && formula.startsWith("=");
  const fmt = typeof numberFormat === "string" ? numberFormat : "";
  if (hasFormula) {
    return {
      category: "formula",
      hasFormula: true,
      formula: formula as string,
      numberFormat: fmt,
      value,
      text: typeof value === "string" ? value.trim().toLowerCase() : "",
    };
  }
  switch (vt) {
    case Excel.RangeValueType.empty:
      return {
        category: "empty",
        hasFormula: false,
        formula: "",
        numberFormat: fmt,
        value: null,
        text: "",
      };
    case Excel.RangeValueType.boolean:
      return {
        category: "boolean",
        hasFormula: false,
        formula: "",
        numberFormat: fmt,
        value,
        text: "",
      };
    case Excel.RangeValueType.error:
      return {
        category: "error",
        hasFormula: false,
        formula: "",
        numberFormat: fmt,
        value,
        text: "",
      };
    case Excel.RangeValueType.string: {
      const s = typeof value === "string" ? value : String(value ?? "");
      return {
        category: s.trim().length === 0 ? "empty" : "text",
        hasFormula: false,
        formula: "",
        numberFormat: fmt,
        value,
        text: s.trim().toLowerCase(),
      };
    }
    case Excel.RangeValueType.double:
    case Excel.RangeValueType.integer:
      return {
        category: isDateFormat(fmt) ? "date" : "numeric",
        hasFormula: false,
        formula: "",
        numberFormat: fmt,
        value,
        text: "",
      };
    default:
      return {
        category: "empty",
        hasFormula: false,
        formula: "",
        numberFormat: fmt,
        value: null,
        text: "",
      };
  }
}

/**
 * Quick heuristic: does this R1C1 formula reference any cell in a row above
 * the current cell's row? R1C1 row references can be:
 *   - `R[-n]` (relative, negative → above)
 *   - `R[n]`  (relative, positive → below; zero → same)
 *   - `Rn`    (absolute row n; if n < current, above)
 *   - `R` alone followed by `C` (same row)
 * Sheet-qualified ranges like `'Other'!R[-1]C` work the same way once the
 * quoted sheet name is skipped.
 *
 * `currentRow0Based` is row index within the region; the function adds the
 * region's minRow offset to get sheet-absolute (1-based) for absolute-row
 * comparisons.
 */
function formulaReferencesAbove(
  formula: string,
  currentRow0Based: number,
  regionMinRow: number
): boolean {
  const absCurrent = regionMinRow + currentRow0Based + 1; // 1-based
  const f = formula.startsWith("=") ? formula.slice(1) : formula;
  const len = f.length;
  let i = 0;

  while (i < len) {
    const c = f[i];
    // Skip string literals
    if (c === '"') {
      i++;
      while (i < len) {
        if (f[i] === '"') {
          if (f[i + 1] === '"') {
            i += 2;
            continue;
          }
          i++;
          break;
        }
        i++;
      }
      continue;
    }
    // Skip quoted sheet-name spans
    if (c === "'") {
      i++;
      while (i < len) {
        if (f[i] === "'") {
          if (f[i + 1] === "'") {
            i += 2;
            continue;
          }
          i++;
          break;
        }
        i++;
      }
      continue;
    }

    // R / r followed by either [offset] or absolute digits or C
    if (c === "R" || c === "r") {
      const next = f[i + 1];
      if (next === "[") {
        const closeIdx = f.indexOf("]", i + 2);
        if (closeIdx > 0) {
          const offset = parseInt(f.substring(i + 2, closeIdx), 10);
          if (!Number.isNaN(offset) && offset < 0) return true;
          i = closeIdx + 1;
          continue;
        }
      } else if (next >= "0" && next <= "9") {
        let j = i + 2;
        while (j < len && f[j] >= "0" && f[j] <= "9") j++;
        const rowNum = parseInt(f.substring(i + 1, j), 10);
        if (!Number.isNaN(rowNum) && rowNum < absCurrent) return true;
        i = j;
        continue;
      }
      // Plain `R` (followed by `C` typically) — no row ref; advance one
      i++;
      continue;
    }

    i++;
  }
  return false;
}

function clamp01(v: number): number {
  if (Number.isNaN(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

function resolveTarget<T>(
  scores: Map<T, ClassificationSignal[]>,
  fallback: T
): { value: T; confidence: number; signals: ClassificationSignal[] } {
  let total = 0;
  let bestValue: T = fallback;
  let bestScore = 0;
  const allSignals: ClassificationSignal[] = [];
  for (const [value, list] of scores) {
    const score = list.reduce((s, sig) => s + sig.contribution, 0);
    total += score;
    allSignals.push(...list);
    if (score > bestScore) {
      bestScore = score;
      bestValue = value;
    }
  }
  const confidence = total > 0 ? bestScore / total : 0;
  // Sort signals by contribution desc for readable display
  allSignals.sort((a, b) => b.contribution - a.contribution);
  return { value: bestValue, confidence, signals: allSignals };
}
